import { useState } from "react";

import type { AlertPolicy, AlertType, Collaborator, RouteConfig } from "../../../queries";
import { useAlertPolicyMutations } from "../../../queries";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";

type RoutingChannel = "email" | "telegram" | "both";

export function RoutingMatrix({
  alertTypes,
  routes,
  collaborators,
  policies,
}: {
  alertTypes: AlertType[];
  routes: RouteConfig[];
  collaborators: Collaborator[];
  policies: AlertPolicy[];
}) {
  const [editing, setEditing] = useState<{ alertType: AlertType; route: RouteConfig; policy?: AlertPolicy } | null>(null);
  const activeRoutes = routes.filter((route) => route.is_active);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Routing Matrix</CardTitle>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <table className="w-full min-w-[880px] text-sm">
          <thead>
            <tr>
              <th className="sticky left-0 bg-white py-2 pr-4 text-left text-xs uppercase tracking-[0.12em] text-slate-500">Alert</th>
              {activeRoutes.map((route) => (
                <th key={route.id} className="py-2 pr-3 text-left text-xs uppercase tracking-[0.12em] text-slate-500">
                  {route.code}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {alertTypes.map((type) => (
              <tr key={type.id}>
                <td className="sticky left-0 bg-white py-3 pr-4 font-medium text-slate-950">{type.label}</td>
                {activeRoutes.map((route) => {
                  const policy = policies.find((row) => row.alert_type_id === type.id && row.route_id === route.id && row.is_active);
                  return (
                    <td key={route.id} className="py-2 pr-3">
                      <button
                        type="button"
                        className={`min-h-20 w-full rounded-md border p-2 text-left ${policy ? "border-blue-200 bg-blue-50" : "border-dashed border-slate-200 bg-white"}`}
                        onClick={() => setEditing({ alertType: type, route, policy })}
                      >
                        {policy ? (
                          <div className="space-y-1">
                            <Badge variant={policy.channel === "email" ? "default" : policy.channel === "telegram" ? "green" : "slate"}>
                              {policy.channel}
                            </Badge>
                            <div className="text-xs text-slate-600">
                              {policy.recipients.map((recipient) => recipient.collaborator_name).filter(Boolean).join(", ") || "No recipients"}
                            </div>
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400">No policy</span>
                        )}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        {editing ? (
          <RoutingEditor
            alertType={editing.alertType}
            route={editing.route}
            policy={editing.policy}
            collaborators={collaborators}
            onClose={() => setEditing(null)}
          />
        ) : null}
      </CardContent>
    </Card>
  );
}

function RoutingEditor({
  alertType,
  route,
  policy,
  collaborators,
  onClose,
}: {
  alertType: AlertType;
  route: RouteConfig;
  policy?: AlertPolicy;
  collaborators: Collaborator[];
  onClose: () => void;
}) {
  const savePolicy = useAlertPolicyMutations().save;
  const [channel, setChannel] = useState<RoutingChannel>(policy?.channel || "both");
  const [selected, setSelected] = useState<number[]>(policy?.recipients.map((recipient) => recipient.collaborator_id) || []);

  const eligible = collaborators.filter((person) => {
    if (!person.is_active) return false;
    if (channel === "email") return Boolean(person.email);
    if (channel === "telegram") return Boolean(person.telegram_chat_id);
    return Boolean(person.email || person.telegram_chat_id);
  });

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-slate-950/30">
      <div className="h-full w-full max-w-md overflow-y-auto bg-white p-5 shadow-xl">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-semibold text-slate-950">{alertType.label}</h3>
            <p className="text-sm text-slate-500">{route.code} - {route.name}</p>
          </div>
          <Button variant="ghost" onClick={onClose}>Close</Button>
        </div>

        <label className="mt-5 block">
          <span className="text-xs font-medium text-slate-600">Channel</span>
          <select
            className="mt-1 h-10 w-full rounded-md border border-slate-200 px-3 text-sm"
            value={channel}
            onChange={(event) => setChannel(event.target.value as RoutingChannel)}
          >
            <option value="both">email + telegram</option>
            <option value="email">email</option>
            <option value="telegram">telegram</option>
          </select>
        </label>

        <div className="mt-5 space-y-2">
          <div className="text-xs font-medium text-slate-600">Recipients</div>
          {eligible.map((person) => (
            <label key={person.id} className="flex items-center gap-2 rounded-md border border-slate-100 p-2 text-sm">
              <input
                type="checkbox"
                checked={selected.includes(person.id)}
                onChange={(event) => {
                  setSelected((value) => (
                    event.target.checked
                      ? [...value, person.id]
                      : value.filter((id) => id !== person.id)
                  ));
                }}
              />
              <span>{person.name}</span>
            </label>
          ))}
          {eligible.length === 0 ? <div className="text-sm text-slate-500">No active recipients for this channel.</div> : null}
        </div>

        <Button
          className="mt-5 w-full"
          disabled={savePolicy.isPending}
          onClick={() => {
            savePolicy.mutate({
              id: policy?.id,
              payload: {
                alert_type_id: alertType.id,
                route_id: route.id,
                channel,
                recipients: selected.map((id) => ({ collaborator_id: id, channel, recipient_kind: "to" })),
                is_active: true,
              },
            }, { onSuccess: onClose });
          }}
        >
          Save routing
        </Button>
      </div>
    </div>
  );
}
